/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

/*!
 * Alfresco Example Content Application - SkyPlus Customization
 * Updated with Document Library Component
 */

import { BrowserModule } from '@angular/platform-browser';
import { NgModule } from '@angular/core';
import { BrowserAnimationsModule, NoopAnimationsModule } from '@angular/platform-browser/animations';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AuthGuard, AuthModule, CoreModule, TRANSLATION_PROVIDER } from '@alfresco/adf-core';
import { ContentModule } from '@alfresco/adf-content-services';
import { AppService } from '@alfresco/aca-shared';
import { CommonModule } from '@angular/common';

import { AppExtensionsModule } from './extensions.module';
import { environment } from '../environments/environment';

import { registerLocaleData } from '@angular/common';
import localeFr from '@angular/common/locales/fr';
import localeDe from '@angular/common/locales/de';
import localeIt from '@angular/common/locales/it';
import localeEs from '@angular/common/locales/es';
import localeJa from '@angular/common/locales/ja';
import localeNl from '@angular/common/locales/nl';
import localePt from '@angular/common/locales/pt';
import localeNb from '@angular/common/locales/nb';
import localeRu from '@angular/common/locales/ru';
import localeCh from '@angular/common/locales/zh';
import localeAr from '@angular/common/locales/ar';
import localeCs from '@angular/common/locales/cs';
import localePl from '@angular/common/locales/pl';
import localeFi from '@angular/common/locales/fi';
import localeDa from '@angular/common/locales/da';
import localeSv from '@angular/common/locales/sv';
import { TranslateModule } from '@ngx-translate/core';
import { RouterModule } from '@angular/router';
import { AppComponent } from './app.components';
import { CONTENT_LAYOUT_ROUTES, ContentServiceExtensionModule, ContentUrlService, CoreExtensionsModule } from '@alfresco/aca-content';
import { ContentVersionService } from '@alfresco/adf-content-services';
import { SHELL_APP_SERVICE, SHELL_AUTH_TOKEN, ShellModule } from '@alfresco/adf-core/shell';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { APP_ROUTES } from './app.routes';
import { MatIconRegistry } from '@angular/material/icon';
import { MatStepperModule } from '@angular/material/stepper';

// SkyPlus Custom Components
import { SkyPlusLoginComponent } from './components/skyplus/login/skyplus-login.component';
import { SkyPlusDashboardComponent } from './components/skyplus/dashboard/skyplus-dashboard.component';
import { SkyPlusDocumentLibraryComponent } from './components/skyplus/document-library/skyplus-document-library.component';
import { SkyPlusProfileComponent } from './components/skyplus/profile/skyplus-profile.component';
import { IndexingService } from './components/skyplus/indexing/indexing.service';
import { IndexingQueueComponent } from './components/skyplus/indexing/indexing-queue.component';
import { SkyPlusSearchComponent } from './components/skyplus/search/skyplus-search.component';
import { SkyPlusReportsComponent } from './components/skyplus/reports/skyplus-reports.component';
//import { SkyPlusWorkflowComponent } from './components/skyplus/workflow/skyplus-workflow.component';

import { SkyPlusWarehouseComponent } from './components/skyplus/warehouse/skyplus-warehouse.component';
import { CaseDashboardComponent } from './components/skyplus/case-management/dashboard/case-dashboard.component';
import { CaseListComponent }      from './components/skyplus/case-management/list/case-list.component';
import { CaseDetailComponent }    from './components/skyplus/case-management/detail/case-detail.component';
import { CaseNewComponent }       from './components/skyplus/case-management/wizard/case-new.component';
import { CaseDocActionsComponent } from './components/skyplus/case-management/detail/case-doc-actions.component';
import { CaseAddPanelComponent } from './components/skyplus/case-management/detail/case-add-panel.component';
import { SkyPlusContractsComponent } from './components/skyplus/contracts/skyplus-contracts.component';
import { SkyPlusContractWizardComponent } from './components/skyplus/contract-wizard/skyplus-contract-wizard.component';

import { ContractPreviewComponent } from './components/skyplus/contracts/contract-preview.component';
import { ContractDetailsComponent } from './components/skyplus/contracts/contract-details.component';
import { ExecutedContractUploadComponent } from './components/skyplus/contracts/executed-contract-upload.component';

import { ContractWorkflowModule } from './components/skyplus/contract-workflow/contract-workflow.module';

import { MailWorkflowModule } from './components/skyplus/mail-workflow/mail-workflow.module';
import { SessionTimeoutDialogComponent } from './components/skyplus/session-timeout/session-timeout-dialog.component';

import { SpSharedModule } from './components/skyplus/shared/sp-shared.module';

import { MenuService } from './components/skyplus/shared/menu/menu.service';
import { MenuGuard }   from './components/skyplus/shared/guards/menu.guard';

import { FeatureGuard } from './components/skyplus/shared/sector/feature.guard';

import { HttpClientModule } from '@angular/common/http';
import { FlipViewerComponent }  from './components/skyplus/flip-viewer/flip-viewer.component';

import { IntelligenceComponent } from './components/skyplus/intelligence/intelligence.component';
import { MarkdownPipe }          from './components/skyplus/intelligence/markdown.pipe';

// Pipes
import { SafePipe } from './pipes/safe.pipe';

registerLocaleData(localeFr);
registerLocaleData(localeDe);
registerLocaleData(localeIt);
registerLocaleData(localeEs);
registerLocaleData(localeJa);
registerLocaleData(localeNl);
registerLocaleData(localePt);
registerLocaleData(localeNb);
registerLocaleData(localeRu);
registerLocaleData(localeCh);
registerLocaleData(localeAr);
registerLocaleData(localeCs);
registerLocaleData(localePl);
registerLocaleData(localeFi);
registerLocaleData(localeDa);
registerLocaleData(localeSv);

@NgModule({
  imports: [
    BrowserModule,
    CommonModule,
    MatStepperModule,
    FormsModule,          // Added for ngModel in search box
    ReactiveFormsModule,
    ContentModule.forRoot(),  // Provides NodesApiService, SearchService, ContentService
    TranslateModule.forRoot(),
    CoreModule.forRoot(),
    CoreExtensionsModule.forRoot(),
    environment.e2e ? NoopAnimationsModule : BrowserAnimationsModule,
    !environment.production ? StoreDevtoolsModule.instrument({ maxAge: 25 }) : [],
    RouterModule.forRoot(APP_ROUTES, {
      useHash: true,
      enableTracing: false
    }),
    AppExtensionsModule,
    ShellModule.withRoutes({
      shellChildren: [CONTENT_LAYOUT_ROUTES]
    }),
    ContentServiceExtensionModule,
     ContractWorkflowModule,
     MailWorkflowModule,
    SpSharedModule,
    CaseDocActionsComponent,
    CaseAddPanelComponent,
    AuthModule.forRoot({ useHash: true })
  ],
  
  providers: [
    IndexingService,
    MenuService,
    MenuGuard,
    FeatureGuard,
    HttpClientModule,
    { provide: ContentVersionService, useClass: ContentUrlService },
    { provide: SHELL_APP_SERVICE, useClass: AppService },
    { provide: SHELL_AUTH_TOKEN, useValue: AuthGuard },
    {
      provide: TRANSLATION_PROVIDER,
      multi: true,
      useValue: { name: 'app', source: 'assets' }
    }
  ],
  declarations: [
    AppComponent,
    SkyPlusLoginComponent,
    SkyPlusDashboardComponent,
    SkyPlusDocumentLibraryComponent,
    SkyPlusProfileComponent,
    SafePipe,
    IndexingQueueComponent,
    SkyPlusSearchComponent,
    SkyPlusReportsComponent,   
    SkyPlusWarehouseComponent,
    CaseDashboardComponent,
    CaseListComponent,
    CaseDetailComponent,
    CaseNewComponent,
    SkyPlusContractsComponent,
    SkyPlusContractWizardComponent,    
    ContractPreviewComponent,
    ContractDetailsComponent,
    ExecutedContractUploadComponent,
    SessionTimeoutDialogComponent,
    FlipViewerComponent,
    IntelligenceComponent,
    MarkdownPipe
    
  ],
  bootstrap: [AppComponent]
})
export class AppModule {
  constructor(matIconRegistry: MatIconRegistry) {
    matIconRegistry.setDefaultFontSetClass('material-icons-outlined');
  }
}
